import 'react-loading-skeleton/dist/skeleton.css';
import { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import dayjs from 'dayjs';

import {
  AgendaItemsRoot,
  BorderedWrapper,
  LogoImage,
  SessionItemContentSection,
  SessionDescription,
  SessionDescriptionText,
  SessionItemHeadlineSection,
  SessionItemHeadlineItem,
  SessionItemHeadlineItemText,
  SessionTitle,
  SessionTitleWrapper,
  SpeakersWrapper,
  Tag,
  Tags,
  SessionInfoWrapper,
  SessionTitleRemaningText,
  SessionContentWrapper,
  SessionInfoDetailsWrapper,
  ToggableContent,
  ToggleWrapper,
  ToggleButton,
  FlexWrapper,
} from './styles';

import { Column } from '@components/LinearLayout';
import Icon from '@components/Icon';
import SpeakerItem from '@composites/SpeakerItem';

import { capitalizeFirstLetter, getImageSrc } from '@utils/fn';
import { sanitizeHtmlWithoutTargetAttrInAnchor } from '@utils/sanitize';
import { formatDate, getSessionTimeWithoutTimezone } from '@utils/time';
import { usePublicCoLocatedEventsByEventId } from '@utils/apiHooks';
import { getEventDetails } from '@store/slices/eventSlice';
import { AgendaDays } from '@pages/Agenda/helper';

import {
  PublicSessionItem,
  PublicTrackItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useMobileWidth } from '@utils/hooks';
import {
  CompanyLogo,
  ProfilePhoto,
  TopSideSpeaker,
} from '@composites/SpeakerItem/styles';
import { htmlToText } from '@utils/htmlParser';

interface AgendaItemProps {
  agendaDaysFormat?: string[];
  activeDay?: string;
  agendasTracks?: PublicTrackItem[];
  isLoading?: boolean;
  session?: PublicSessionItem;
  minimalDetails?: boolean;
  displayLogoForEventId?: string;
}

/*
 * Mapping for Session Sub Type
 */
export const sessionKeys = [
  'icebreaker',
  'registrierung_community_wall',
  'say_hello',
  'track',
  'break',
  'challenge_your_peers',
  'dinner',
  'morning_coffee_club',
  'world_cafes_description',
  'world_cafes_round_1_3',
  'world_cafes_round_4_5',
  'world_cafes_round_harvesting',
  'panel_discussion',
  'fishbowl_discussion',
  'tech_take',
  'morning_session',
  'start_up_session',
  'training_session',
  'audit_debatte',
  'negotiation_workshop',
  'award',
];

export type SessionIconKey = keyof typeof sessionIcons;

export const sessionIcons = {
  made_with_love:
    'https://media.we-conect.com/images/icons/sessions/Made+with+love_752x360.png',
  icebreaker:
    'https://media.we-conect.com/images/icons/sessions/we_Icebreaker.png',
  registrierung_community_wall:
    'https://media.we-conect.com/images/icons/sessions/we_Offline-Facebook.png',
  say_hello:
    'https://media.we-conect.com/images/icons/sessions/we_Say-Hello.png',
  track: 'https://media.we-conect.com/images/icons/sessions/we_CaseStudy.png',
  break:
    'https://media.we-conect.com/images/icons/sessions/we_Networking-Zone.png',
  challenge_your_peers:
    'https://media.we-conect.com/images/icons/sessions/we_Challenge-Your-Peers.png',
  dinner: 'https://media.we-conect.com/images/icons/sessions/we_Dinner.png',
  morning_coffee_club:
    'https://media.we-conect.com/images/icons/sessions/we_Breakfast-Club.png',
  world_cafes_description:
    'https://media.we-conect.com/images/icons/sessions/we_World-Cafe.png',
  world_cafes_round_1_3:
    'https://media.we-conect.com/images/icons/sessions/we_World-Cafe.png',
  world_cafes_round_4_5:
    'https://media.we-conect.com/images/icons/sessions/we_World-Cafe.png',
  world_cafes_round_harvesting:
    'https://media.we-conect.com/images/icons/sessions/we_World-Cafe.png',
  panel_discussion:
    'https://media.we-conect.com/images/icons/sessions/we_Panel.png',
  fishbowl_discussion:
    'https://media.we-conect.com/images/icons/sessions/we_Fishbowl.png',
  tech_take:
    'https://media.we-conect.com/images/icons/sessions/we_Tech-Take.png',
  morning_session:
    'https://media.we-conect.com/images/icons/sessions/we_Morning-Session.png',
  start_up_session:
    'https://media.we-conect.com/images/icons/sessions/we_StartUp-Lab.png',
  training_session:
    'https://media.we-conect.com/images/icons/sessions/we_Training-Session.png',
  audit_debatte:
    'https://media.we-conect.com/images/icons/sessions/we_Audit-Debatte.png',
  negotiation_workshop:
    'https://media.we-conect.com/images/icons/sessions/we_Partnering-Session.png',
  award: 'https://media.we-conect.com/images/icons/sessions/we_Award.png',
};

/* -------------------------------------------------------
 * Helper: Extract Session Event ID
 * ----------------------------------------------------- */
const getSessionEventId = (session?: PublicSessionItem): string | undefined => {
  const event = session?.event as any;
  if (!event) return undefined;
  if (typeof event === 'string') return event;
  if (typeof event === 'object')
    return event?.id || event?.event_id || event?.eventId;
  return undefined;
};

/* -------------------------------------------------------
 * Main Component
 * ----------------------------------------------------- */
const AgendaItemV2 = ({
  agendaDaysFormat,
  activeDay,
  session,
  agendasTracks,
  isLoading,
  minimalDetails,
  displayLogoForEventId,
}: AgendaItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isMobile = useMobileWidth();

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: Boolean(sluggedEvent?.id?.length && sluggedEvent?.colocated),
    });

  const isAgendaLoading = isLoading || isCoLocatedEventsLoading;

  /* -------------------------------------------------------
   * Derived Data
   * ----------------------------------------------------- */
  const forcedEventForLogo = useMemo(() => {
    if (!displayLogoForEventId) return undefined;
    if (displayLogoForEventId === sluggedEvent?.id) return sluggedEvent;
    return coLocatedEvents?.find(e => e.id === displayLogoForEventId);
  }, [displayLogoForEventId, sluggedEvent, coLocatedEvents]);

  const eventFromSession = useMemo(() => {
    if (!sluggedEvent?.colocated) return sluggedEvent;

    if (forcedEventForLogo) return forcedEventForLogo;

    const eventId = getSessionEventId(session);
    const byCo = coLocatedEvents?.find(e => e.id === eventId);
    if (byCo) return byCo;

    return sluggedEvent;
  }, [sluggedEvent, coLocatedEvents, session, forcedEventForLogo]);

  const targetEventSlug =
    sluggedEvent?.slug ||
    (typeof session?.event === 'object' && (session?.event as any)?.slug) ||
    eventFromSession?.slug;

  const sessionEventId = getSessionEventId(session);

  const sourceEventIdForPrevious =
    sessionEventId && sessionEventId !== sluggedEvent?.id
      ? sessionEventId
      : undefined;

  const matchingIndex = agendaDaysFormat?.findIndex(d => d === activeDay) ?? 0;

  /* -------------------------------------------------------
   * Session Info Items
   * ----------------------------------------------------- */
  const sessionDetailsItems = useMemo(() => {
    const time = getSessionTimeWithoutTimezone(
      session?.start_date ?? '',
      session?.end_date ?? ''
    );

    if (minimalDetails) return [{ icon: 'clock', value: time }];

    return [
      { icon: 'agenda', value: capitalizeFirstLetter(String(session?.type)) },
      { icon: 'clock', value: time },
      { icon: 'location', value: session?.location?.name },
      { icon: 'expectations', value: 'Solution Study' },
    ];
  }, [minimalDetails, session]);

  /* -------------------------------------------------------
   * Title Splitter
   * ----------------------------------------------------- */
  const { sessionName, sessionText } = useMemo(() => {
    if (!session?.title) return { sessionName: '', sessionText: '' };
    const [name, ...rest] = session.title.split(':');
    return { sessionName: name.trim(), sessionText: rest.join(':').trim() };
  }, [session?.title]);

  /* -------------------------------------------------------
   * State: Togglable Description
   * ----------------------------------------------------- */
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [hasSpeaker, setHasSpeaker] = useState(
    !!(session?.speakers && session.speakers.length > 0)
  );
  const subType = (session as any)?.sub_type as SessionIconKey;
  const isValidIcon = subType && sessionIcons[subType];

  const toggleContent = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  /* -------------------------------------------------------
   * Render Helpers
   * ----------------------------------------------------- */
  const renderSpeakers = () =>
    session?.speakers?.map(speaker => (
      <SpeakerItem
        key={speaker.profile}
        speaker={speaker}
        color={eventFromSession?.design?.themeColor}
        isAgendaLoading={isAgendaLoading}
        eventSlug={targetEventSlug}
        sourceEventIdForPrevious={sourceEventIdForPrevious}
      />
    ));

  const renderTags = () =>
    isAgendaLoading ? (
      <Tags>
        {Array.from({ length: 3 }).map((_, idx) => (
          <Skeleton
            key={idx}
            enableAnimation
            width={`${5 + idx * 2}rem`}
            borderRadius={theme.borderRadius.radius20}
          />
        ))}
      </Tags>
    ) : (
      <Tags>
        {session?.tags
          ?.split(',')
          ?.filter((e: any) => e)
          ?.map((tag: string) => (
            <Tag
              key={tag}
              $color={eventFromSession?.design?.secondaryThemeColor}
            >
              {tag}
            </Tag>
          ))}
      </Tags>
    );
  const isThereTagsToRender = Boolean(
    session?.tags?.split(',').filter((e: any) => e)?.length
  );

  const renderDescription = () =>
    isAgendaLoading ? (
      <Column $gap="1rem">
        <Skeleton enableAnimation width="100%" />
        <Skeleton enableAnimation width="50%" />
        <Skeleton enableAnimation width="80%" />
      </Column>
    ) : (
      <SessionDescriptionText
        dangerouslySetInnerHTML={{
          __html: sanitizeHtmlWithoutTargetAttrInAnchor(
            String(
              isMobile && !hasSpeaker
                ? session?.description?.replace(/<img[^>]*\/?>/gi, '')
                : session?.description
            )
          ),
        }}
      />
    );
  const isThereDescriptionToRender = Boolean(
    htmlToText(session?.description ?? '')?.length
  );

  return (
    <article>
      <AgendaItemsRoot>
        {/* Header Section */}
        {isAgendaLoading ? (
          <Skeleton
            count={1}
            enableAnimation
            width="16rem"
            height="33rem"
            borderRadius={theme.borderRadius.radius10}
          />
        ) : (
          <SessionItemHeadlineSection>
            <SessionItemHeadlineItem
              padding={isMobile ? '0.5rem 2.5rem' : '1rem 3.5rem'}
            >
              <LogoImage
                src={getImageSrc(eventFromSession?.design?.eventLogoPosSvg)}
                alt="Event Logo"
              />
            </SessionItemHeadlineItem>

            {!isMobile && (
              <SessionItemHeadlineItem
                color={eventFromSession?.design?.themeColor}
              >
                <SessionItemHeadlineItemText
                  color={eventFromSession?.design?.themeColor}
                >
                  {t(AgendaDays[matchingIndex])?.toUpperCase()}
                  {activeDay &&
                    ` - ${dayjs(formatDate(activeDay)).format('DD.MM.YYYY')}`}
                </SessionItemHeadlineItemText>
              </SessionItemHeadlineItem>
            )}

            {sessionDetailsItems?.[0]?.value && (
              <SessionItemHeadlineItem>
                <SessionItemHeadlineItemText
                  color={eventFromSession?.design?.themeColor}
                >
                  {sessionDetailsItems[0].value}
                </SessionItemHeadlineItemText>
              </SessionItemHeadlineItem>
            )}
          </SessionItemHeadlineSection>
        )}

        {/* Content Section */}
        <BorderedWrapper>
          <SessionItemContentSection>
            <SessionDescription $gap={sessionText?.length > 0 ? '3rem' : '0'}>
              <SessionTitleWrapper
                $color={eventFromSession?.design?.themeColor}
              >
                <SessionTitle>{sessionName}</SessionTitle>

                <SessionInfoWrapper gap="3rem">
                  <SessionInfoDetailsWrapper key={'1'} gap="1rem">
                    <Icon
                      size={24}
                      name={sessionDetailsItems?.[1]?.icon}
                      color={eventFromSession?.design?.themeColor}
                      indentLess
                    />
                    <FlexWrapper>
                      {isMobile ? (
                        <span>
                          <strong
                            style={{ fontWeight: theme.fontWeight.weight900 }}
                          >{`${t(AgendaDays[matchingIndex])?.toUpperCase()} | `}</strong>
                          {`${sessionDetailsItems?.[1]?.value ?? ''}`}
                        </span>
                      ) : (
                        <span>{`${sessionDetailsItems?.[1]?.value ?? ''}`}</span>
                      )}
                    </FlexWrapper>
                  </SessionInfoDetailsWrapper>

                  {sessionDetailsItems?.[2]?.value && (
                    <SessionInfoDetailsWrapper key={'2'} gap="1rem">
                      <Icon
                        size={24}
                        name={sessionDetailsItems?.[2]?.icon}
                        color={eventFromSession?.design?.themeColor}
                        indentLess
                      />
                      <div>{sessionDetailsItems?.[2]?.value}</div>
                    </SessionInfoDetailsWrapper>
                  )}
                </SessionInfoWrapper>
              </SessionTitleWrapper>

              <SessionContentWrapper>
                {sessionText?.length > 0 ? (
                  <SessionTitleRemaningText>
                    {sessionText}
                  </SessionTitleRemaningText>
                ) : (
                  <div
                    style={{
                      height: isMobile ? '2rem' : '3rem',
                      width: '100%',
                    }}
                  >
                    {''}
                  </div>
                )}

                {hasSpeaker ? (
                  <SpeakersWrapper>{renderSpeakers()}</SpeakersWrapper>
                ) : (
                  <TopSideSpeaker
                    gridTemplateColumns={isMobile ? '1fr' : '1fr auto'}
                  >
                    {isValidIcon && (
                      <Column $gap="0">
                        <ProfilePhoto
                          src={getImageSrc(sessionIcons[subType])}
                          alt=""
                          loading="lazy"
                        />
                        <CompanyLogo
                          src={getImageSrc(sessionIcons.made_with_love)}
                          alt="made_with_love-icon"
                          loading="lazy"
                        />
                      </Column>
                    )}

                    {!isMobile && isValidIcon && (
                      <Column
                        $gap={isMobile ? '0.8rem' : '1.2rem'}
                        $justifyContent="flex-start"
                        margin={`${isMobile ? '1rem' : '2rem'} 0 0 0`}
                      >
                        <SessionDescriptionText
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtmlWithoutTargetAttrInAnchor(
                              String(
                                session?.description?.replace(
                                  /<img[^>]*>/gi,
                                  ''
                                )
                              )
                            ),
                          }}
                        />
                      </Column>
                    )}
                  </TopSideSpeaker>
                )}
              </SessionContentWrapper>
            </SessionDescription>
          </SessionItemContentSection>

          {/* Toggle Section */}
          {(hasSpeaker || !isValidIcon || isMobile) &&
            (isThereDescriptionToRender || isThereTagsToRender) && (
              <>
                <ToggleWrapper>
                  <ToggleButton
                    onClick={toggleContent}
                    themeColor={eventFromSession?.design?.themeColor}
                  >
                    <Icon
                      name={`event-pages_${isExpanded ? 'up' : 'down'}-arrow`}
                      color={eventFromSession?.design?.themeColor}
                      indentLess
                      size={32}
                    />
                  </ToggleButton>
                </ToggleWrapper>

                <ToggableContent $isVisible={isExpanded}>
                  {renderDescription()}
                  {(session?.tags?.length ?? 0) > 0 && renderTags()}
                </ToggableContent>
              </>
            )}
        </BorderedWrapper>
      </AgendaItemsRoot>
    </article>
  );
};

export default AgendaItemV2;
